import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAccount } from "wagmi";

import { PaymentSheet } from "@/components/PaymentSheet";
import {
  ASQAmount,
  EmptyState,
  ErrorState,
  Panel,
  SheetLabel,
  Skeleton,
  Status,
} from "@/components/primitives";
import { api, type Voucher } from "@/lib/api";
import { formatASQ, formatDate } from "@/lib/format";
import { usePayment } from "@/lib/use-payment";

/**
 * E-Voucher — plan §18, §37A.
 *
 * The metaphor is a premium ticket, so the card carries a serial, a perforated
 * edge and an expiry rail rather than an e-commerce coupon shape. Filters sit
 * in a vertical rail on the left (§62) rather than as a row of pills, which
 * keeps the grid from restarting every time a filter changes.
 */
export function VouchersPage() {
  const { address, isConnected } = useAccount();
  const queryClient = useQueryClient();
  const [category, setCategory] = useState<string>("Semua");
  const [selected, setSelected] = useState<Voucher | null>(null);
  const { state, pay, reset } = usePayment();

  const { data, isPending, isError } = useQuery({
    queryKey: ["vouchers"],
    queryFn: api.vouchers,
  });

  const { data: owned } = useQuery({
    queryKey: ["my-vouchers", address],
    queryFn: () => api.myVouchers(address!),
    enabled: Boolean(address),
  });

  const filtered = useMemo(() => {
    const vouchers = data?.vouchers ?? [];
    return category === "Semua"
      ? vouchers
      : vouchers.filter((voucher) => voucher.category === category);
  }, [data, category]);

  const utility = data?.utility;

  const closeSheet = () => {
    if (state.step === "done") {
      queryClient.invalidateQueries({ queryKey: ["vouchers"] });
      queryClient.invalidateQueries({ queryKey: ["my-vouchers", address] });
      queryClient.invalidateQueries({ queryKey: ["utilities"] });
    }
    setSelected(null);
    reset();
  };

  return (
    <div className="sheet py-16">
      <SheetLabel coordinate="ASETQU_EVOUCHER">Benefit pass</SheetLabel>
      <h1 className="display-section mt-5 text-paper-50">E-Voucher</h1>
      <p className="mt-5 max-w-xl text-base text-paper-300/70">
        Voucher benefit untuk ekosistem properti. Ditukar dengan ASQ; kodenya
        terbit setelah event pembayaran diverifikasi, bukan saat Anda menekan
        tombol.
      </p>

      {data?.unavailable ? (
        <div className="mt-10">
          <ErrorState title="E-Voucher tidak tersedia" code={data.unavailable.code}>
            {data.unavailable.detail}
          </ErrorState>
        </div>
      ) : null}

      <div className="mt-12 grid gap-10 lg:grid-cols-12">
        {/* Vertical filter rail. */}
        <nav aria-label="Kategori" className="lg:col-span-3">
          <SheetLabel>Kategori</SheetLabel>
          <ul className="mt-4 border-t border-ink-700">
            {["Semua", ...(data?.categories ?? [])].map((entry) => (
              <li key={entry}>
                <button
                  type="button"
                  onClick={() => setCategory(entry)}
                  aria-pressed={category === entry}
                  className={`flex w-full items-center justify-between border-b border-ink-700 py-3 text-left text-sm transition-colors ${
                    category === entry
                      ? "text-hub-700"
                      : "text-paper-300/60 hover:text-paper-50"
                  }`}
                >
                  <span>{entry}</span>
                  {category === entry ? (
                    <span aria-hidden="true" className="h-px w-5 bg-hub-500" />
                  ) : null}
                </button>
              </li>
            ))}
          </ul>

          {owned?.vouchers.length ? (
            <div className="mt-10">
              <SheetLabel>Voucher Anda</SheetLabel>
              <ul className="mt-4 space-y-3">
                {owned.vouchers.slice(0, 4).map((entry) => (
                  <li key={entry.id} className="flex gap-3">
                    <span aria-hidden="true" className="mt-1.5 h-2 w-2 shrink-0 bg-asq-emerald" />
                    <div>
                      <p className="datum text-sm text-paper-50">{entry.code}</p>
                      <p className="micro mt-1">
                        {entry.voucher?.name} · {formatDate(entry.expiresAt)}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </nav>

        <div className="lg:col-span-9">
          {isPending ? (
            <div className="grid gap-5 sm:grid-cols-2">
              <Skeleton className="h-56" />
              <Skeleton className="h-56" />
              <Skeleton className="h-56" />
              <Skeleton className="h-56" />
            </div>
          ) : isError ? (
            <ErrorState title="Tidak dapat memuat voucher" code="LOAD_FAILED">
              Coba muat ulang halaman.
            </ErrorState>
          ) : filtered.length === 0 ? (
            <EmptyState title="Tidak ada voucher di kategori ini">
              Pilih kategori lain, atau lihat semua voucher yang tersedia.
            </EmptyState>
          ) : (
            /*
              Staggered rather than a uniform grid: the second column is offset
              downward on desktop, which is §62's "staggered voucher grid" and
              stops four equal rectangles reading as the template card grid
              §36.10 rejects.
            */
            <ul className="grid gap-5 sm:grid-cols-2">
              {filtered.map((voucher, index) => (
                <li key={voucher.id} className={index % 2 === 1 ? "sm:mt-10" : ""}>
                  <VoucherTicket
                    voucher={voucher}
                    index={index}
                    canPay={Boolean(isConnected && utility?.usable)}
                    onRedeem={() => setSelected(voucher)}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {selected && utility ? (
        <PaymentSheet
          open
          onClose={closeSheet}
          title="Tukar Voucher"
          subject={selected.name}
          details={[
            { label: "Benefit", value: selected.benefit },
            { label: "Kategori", value: selected.category },
            { label: "Masa berlaku", value: `${selected.validityDays} hari` },
          ]}
          amountRaw={selected.priceRaw}
          utility={utility}
          state={state}
          onConfirm={() => pay({ utility: "ASETQU_EVOUCHER", voucherId: selected.id })}
        />
      ) : null}
    </div>
  );
}

/**
 * One voucher, drawn as a ticket.
 *
 * The serial is derived from the voucher's own id rather than invented, so two
 * tickets can never show the same number — a decorative serial that repeats is
 * worse than none.
 */
function VoucherTicket({
  voucher,
  index,
  canPay,
  onRedeem,
}: {
  voucher: Voucher;
  index: number;
  canPay: boolean;
  onRedeem: () => void;
}) {
  const soldOut = voucher.stock !== null && voucher.stock <= 0;
  const serial = `VCR-${String(index + 1).padStart(3, "0")}${voucher.id.slice(-2).toUpperCase()}`;

  return (
    <Panel className="relative flex h-full flex-col overflow-hidden">
      {/* The tear line, with notches punched out of both edges. */}
      <span
        aria-hidden="true"
        className="absolute inset-x-0 top-[58%] border-t border-dashed border-ink-600"
      />
      <span
        aria-hidden="true"
        className="absolute left-0 top-[58%] h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-ink-950"
      />
      <span
        aria-hidden="true"
        className="absolute right-0 top-[58%] h-3 w-3 translate-x-1/2 -translate-y-1/2 rounded-full bg-ink-950"
      />

      <div className="flex-1 p-6">
        <div className="flex items-start justify-between gap-3">
          <p className="micro">{voucher.category}</p>
          <p className="micro">{serial}</p>
        </div>

        <h3 className="display-section mt-5 text-lg text-paper-50">{voucher.name}</h3>
        <p className="mt-3 text-sm text-asq-emerald">{voucher.benefit}</p>
      </div>

      <div className="p-6 pt-8">
        <div className="flex items-end justify-between gap-4">
          <div>
            <ASQAmount raw={voucher.priceRaw} size="lg" />
            <p className="micro mt-2">Berlaku {voucher.validityDays} hari</p>
          </div>
          <div className="text-right">
            {soldOut ? (
              <Status state="bad">Habis</Status>
            ) : (
              <p className="micro">
                Stok {voucher.stock === null ? "∞" : voucher.stock}
              </p>
            )}
          </div>
        </div>

        <button
          type="button"
          className="btn btn--primary mt-5 w-full"
          onClick={onRedeem}
          disabled={!canPay || soldOut}
        >
          {soldOut ? "Habis" : "Tukar Voucher"}
        </button>
        {!canPay && !soldOut ? (
          <p className="micro mt-2 text-center">Hubungkan wallet untuk menukar</p>
        ) : null}
      </div>
    </Panel>
  );
}
